export type AnalyticsConsent = "unknown" | "granted" | "denied";

export const ANALYTICS_CONSENT_COOKIE = "veracio_analytics_consent";

const COOKIE_MAX_AGE_SECONDS = 31_536_000;

function cookieValue(document: Document): string | undefined {
  try {
    return document.cookie
      .split(";")
      .map((part) => part.trim())
      .find((part) => part.startsWith(`${ANALYTICS_CONSENT_COOKIE}=`))
      ?.slice(ANALYTICS_CONSENT_COOKIE.length + 1);
  } catch {
    return undefined;
  }
}

function isVeracioHost(hostname: string): boolean {
  const normalized = hostname.toLowerCase().replace(/\.$/, "");
  return normalized === "veracio.app" || normalized.endsWith(".veracio.app");
}

export function readAnalyticsConsent(
  document: Document,
  location: Pick<Location, "hostname">,
): AnalyticsConsent {
  void location;
  const value = cookieValue(document);
  return value === "granted" || value === "denied" ? value : "unknown";
}

export function writeAnalyticsConsent(
  document: Document,
  location: Pick<Location, "hostname" | "protocol">,
  next: Exclude<AnalyticsConsent, "unknown">,
): boolean {
  try {
    const production = location.protocol === "https:" && isVeracioHost(location.hostname);
    const attributes = [
      `${ANALYTICS_CONSENT_COOKIE}=${next}`,
      `Max-Age=${COOKIE_MAX_AGE_SECONDS}`,
      "Path=/",
      "SameSite=Lax",
    ];
    if (production) attributes.push("Domain=.veracio.app", "Secure");
    document.cookie = attributes.join("; ");
    return readAnalyticsConsent(document, location) === next;
  } catch {
    return false;
  }
}

export function subscribeToAnalyticsConsent(view: Window, listener: () => void): () => void {
  const onFocus = (): void => listener();
  const onVisibilityChange = (): void => listener();
  view.addEventListener("focus", onFocus);
  view.document.addEventListener("visibilitychange", onVisibilityChange);

  return () => {
    view.removeEventListener("focus", onFocus);
    view.document.removeEventListener("visibilitychange", onVisibilityChange);
  };
}
